import React, { useState, useRef, useEffect } from 'react';
import { Send, History, Plus, ChevronDown, File, X, Copy, Reply, Edit2, Search, MoreVertical, Trash2, Check, MessageSquare, ChevronLeft, Eye, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import llmService from '../services/llmService.js';
import chatService from '../services/chatService.js';

const ChatDemo = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [modelsError, setModelsError] = useState(null);
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [contextFiles, setContextFiles] = useState([]);
  const [threads, setThreads] = useState({});
  const [activeThread, setActiveThread] = useState(null);
  const [threadInputValue, setThreadInputValue] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const inputRef = useRef(null);
  const threadInputRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Chat management state
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationName, setConversationName] = useState("New Conversation");
  const [isEditingName, setIsEditingName] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [selectedConversations, setSelectedConversations] = useState(new Set());
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showModelInfo, setShowModelInfo] = useState(null); // Track which message's model info is shown
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState(null);
  
  // Mobile navigation state
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchCurrentY, setTouchCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Navigation items for mobile menu
  const navigationItems = [
    {
      id: 'library',
      title: 'Library',
      path: '/prompt-manager',
      icon: '/images/library.png',
      iconGif: '/images/library.gif'
    },
    {
      id: 'tone-shift',
      title: 'Tone Shift',
      path: '/tone-shift',
      icon: '/images/toneshift.png',
      iconGif: '/images/toneshift.gif'
    },
    {
      id: 'chat',
      title: 'Chat',
      path: '/chat',
      icon: '/images/chat.png',
      iconGif: '/images/chat.gif'
    },
    {
      id: 'workflows',
      title: 'Workflows',
      path: '/workflows',
      icon: '/images/workflows.png',
      iconGif: '/images/workflows.gif'
    },
    {
      id: 'prototyper',
      title: 'Prototyper',
      path: '/prototyper',
      icon: '/images/prototype.png',
      iconGif: '/images/prototype.gif'
    }
  ];

  // Touch handlers for mobile navigation modal
  const handleTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchCurrentY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    setTouchCurrentY(currentY);
    
    const deltaY = currentY - touchStartY;
    const modal = e.currentTarget;
    
    if (deltaY > 0) {
      modal.style.transform = `translateY(${deltaY}px)`;
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    
    const deltaY = touchCurrentY - touchStartY;
    const threshold = 100; // Minimum distance to dismiss
    
    if (deltaY > threshold) {
      setShowMobileNav(false);
    }
    
    // Reset modal position
    const modal = e.currentTarget;
    modal.style.transform = '';
    
    setIsDragging(false);
    setTouchStartY(0);
    setTouchCurrentY(0);
  };

  // Fetch available models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      setModelsLoading(true);
      setModelsError(null);
      
      try {
                 const result = await llmService.getProviders();
         if (result.success) {
           const models = [];
           
           // OpenAI pricing per 1K tokens (input/output) - based on current OpenAI pricing
           const openaiPricing = {
             'gpt-3.5-turbo': { input: 0.0015, output: 0.002 },
             'gpt-4': { input: 0.03, output: 0.06 },
             'gpt-4-turbo': { input: 0.01, output: 0.03 },
             'gpt-4o': { input: 0.005, output: 0.015 },
             'gpt-4o-mini': { input: 0.00015, output: 0.0006 },
           };
           
           result.data.results.forEach(provider => {
             provider.supported_models.forEach(model => {
               // Better abbreviation logic
               let shortName = '';
               let providerShort = '';
               if (provider.name === 'OpenAI') {
                 providerShort = 'OpenAI';
                 shortName = model.replace('gpt-', '').replace('-turbo', '');
               } else if (provider.name === 'Claude') {
                 providerShort = 'Claude';
                 shortName = model.replace('claude-', '');
               } else {
                 providerShort = provider.name;
                 shortName = model;
               }
               
               // Get pricing info
               const pricing = openaiPricing[model];
               const formatPrice = (price) => {
                 if (price >= 0.001) return price.toFixed(3);
                 if (price >= 0.0001) return price.toFixed(4);
                 return price.toFixed(5);
               };
               const priceDisplay = pricing 
                 ? `$${formatPrice(pricing.input)}/$${formatPrice(pricing.output)} per 1K tokens`
                 : 'Pricing varies';
               
               models.push({
                 id: `${provider.name}::${model}`,
                 name: `${provider.name} - ${model}`,
                 short: `${providerShort}: ${shortName}`,
                 display: `${providerShort} ${shortName}`,
                 provider: provider.name,
                 model: model,
                 pricing: priceDisplay,
                 providerInfo: provider
               });
             });
           });
          
          setAvailableModels(models);
          
          // Set default model (prefer GPT-4o-mini if available, otherwise first available)
          const defaultModel = models.find(m => m.model === 'gpt-4o-mini') || models[0];
          if (defaultModel) {
            setSelectedModel(defaultModel.id);
            setSelectedProvider(defaultModel.provider);
          }
        } else {
          setModelsError(result.error || 'Failed to load models');
        }
      } catch (error) {
        setModelsError('Failed to connect to AI service');
        console.error('Error fetching models:', error);
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, []);

  // Fetch conversations on component mount - but only after models are loaded (auth is established)
  useEffect(() => {
    const fetchConversations = async () => {
      // Don't fetch conversations until models are loaded (indicating auth is working)
      if (modelsLoading || modelsError || availableModels.length === 0) {
        return;
      }

      setConversationsLoading(true);
      setConversationsError(null);
      
      try {
        // Add a small delay to ensure auth is fully established
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const result = await chatService.getConversations();
        if (result.success) {
          const transformedConversations = result.data.results.map(conv => 
            chatService.transformConversation(conv)
          );
          setConversations(transformedConversations);
        } else {
          setConversationsError(result.error || 'Failed to load conversations');
        }
      } catch (error) {
        setConversationsError('Failed to connect to chat service');
        console.error('Error fetching conversations:', error);
      } finally {
        setConversationsLoading(false);
      }
    };

    fetchConversations();
  }, [modelsLoading, modelsError, availableModels.length]); // Depend on model loading state

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, activeThread, threads]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape key closes thread
      if (e.key === 'Escape' && activeThread) {
        handleCloseThread();
      }
      // Escape key also closes model info
      if (e.key === 'Escape' && showModelInfo) {
        setShowModelInfo(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeThread, showModelInfo]);

  // Close model info when new messages are added
  useEffect(() => {
    setShowModelInfo(null);
  }, [messages.length]);

  // Simple markdown renderer
  const renderMarkdown = (text) => {
    // Basic markdown support
    let html = text
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Code inline
      .replace(/`(.*?)`/g, '<code style="background-color: #f3f4f6; padding: 0.125rem 0.25rem; border-radius: 0.25rem; font-family: monospace;">$1</code>')
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre style="background-color: #f3f4f6; padding: 0.75rem; border-radius: 0.375rem; overflow-x: auto; font-family: monospace; white-space: pre-wrap;"><code>$1</code></pre>')
      // Line breaks
      .replace(/\n/g, '<br>');
    
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending || !selectedModel) return;

    setIsSending(true);
    const currentPrompt = inputValue;
    setInputValue('');

    try {
      // If this is the first message and no conversation is selected, create a new one
      let conversationId = currentConversationId;
      if (!conversationId) {
        const result = await chatService.createConversation(
          currentPrompt.slice(0, 50) + (currentPrompt.length > 50 ? "..." : ""),
          selectedProvider || "OpenAI",
          availableModels.find(m => m.id === selectedModel)?.model || "gpt-4o-mini"
        );
        
        if (result.success) {
          const newConversation = chatService.transformConversation(result.data);
          setConversations(prev => [newConversation, ...prev]);
          setCurrentConversationId(newConversation.id);
          setConversationName(newConversation.name);
          conversationId = newConversation.id;
        } else {
          throw new Error(result.error || 'Failed to create conversation');
        }
      }

      // Send message via API
      setIsAiThinking(true);
      const result = await chatService.sendMessage(conversationId, currentPrompt);
      
      if (result.success) {
        // API returns both user message and AI response
        const userMessage = chatService.transformMessage(result.data.user_message);
        const aiMessage = result.data.ai_response ? 
          chatService.transformMessage(result.data.ai_response) : null;
        
        setMessages(prev => {
          const newMessages = [...prev, userMessage];
          if (aiMessage) {
            newMessages.push(aiMessage);
          }
          return newMessages;
        });
        
        // Update conversation list with new preview
        setConversations(prev => prev.map(conv => 
          conv.id === conversationId
            ? { ...conv, preview: currentPrompt.slice(0, 100) + "...", lastMessage: new Date() }
            : conv
        ));
      } else {
        // Handle API error
        const errorMessage = chatService.formatErrorMessage(result.error);
        const userMessage = {
          id: Date.now(),
          text: currentPrompt,
          isUser: true,
          timestamp: new Date(),
          model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel,
          threadCount: 0
        };
        const errorResponse = {
          id: Date.now() + 1,
          text: `Error: ${errorMessage}`,
          isUser: false,
          timestamp: new Date(),
          model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel,
          threadCount: 0,
          isError: true
        };
        setMessages(prev => [...prev, userMessage, errorResponse]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const userMessage = {
        id: Date.now(),
        text: currentPrompt,
        isUser: true,
        timestamp: new Date(),
        model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel,
        threadCount: 0
      };
      const errorResponse = {
        id: Date.now() + 1,
        text: 'Error: Failed to send message. Please try again.',
        isUser: false,
        timestamp: new Date(),
        model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel,
        threadCount: 0,
        isError: true
      };
      setMessages(prev => [...prev, userMessage, errorResponse]);
    } finally {
      setIsAiThinking(false);
      setIsSending(false);
    }
  };

  const handleSendThreadMessage = async () => {
    if (!threadInputValue.trim() || !activeThread || !selectedModel || !currentConversationId) return;

    const currentPrompt = threadInputValue;
    setThreadInputValue('');

    try {
      // Send thread message via API
      const result = await chatService.sendThreadMessage(currentConversationId, activeThread.id, currentPrompt);
      
      if (result.success) {
        // API returns both user message and AI response
        const userMessage = chatService.transformMessage(result.data.user_message);
        const aiMessage = result.data.ai_response ? 
          chatService.transformMessage(result.data.ai_response) : null;
        
        // Add messages to thread
        setThreads(prev => {
          const currentThread = prev[activeThread.id] || [];
          const newThread = [...currentThread, userMessage];
          if (aiMessage) {
            newThread.push(aiMessage);
          }
          return {
            ...prev,
            [activeThread.id]: newThread
          };
        });
        
        // Update thread count on main message with animation trigger
        const newCount = (threads[activeThread.id]?.length || 0) + (aiMessage ? 2 : 1);
        setMessages(prev => prev.map(msg => 
          msg.id === activeThread.id 
            ? { ...msg, threadCount: newCount, animate: true }
            : msg
        ));

        // Reset animation after a brief delay
        setTimeout(() => {
          setMessages(prev => prev.map(msg => 
            msg.id === activeThread.id 
              ? { ...msg, animate: false }
              : msg
          ));
        }, 600);
        
      } else {
        // Handle API error
        const errorMessage = chatService.formatErrorMessage(result.error);
        const userMessage = {
          id: Date.now(),
          text: currentPrompt,
          isUser: true,
          timestamp: new Date(),
          model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel
        };
        const errorResponse = {
          id: Date.now() + 1,
          text: `Error: ${errorMessage}`,
          isUser: false,
          timestamp: new Date(),
          model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel,
          isError: true
        };
        
        setThreads(prev => ({
          ...prev,
          [activeThread.id]: [...(prev[activeThread.id] || []), userMessage, errorResponse]
        }));
      }
    } catch (error) {
      console.error('Error sending thread message:', error);
      const userMessage = {
        id: Date.now(),
        text: currentPrompt,
        isUser: true,
        timestamp: new Date(),
        model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel
      };
      const errorResponse = {
        id: Date.now() + 1,
        text: 'Error: Failed to send message. Please try again.',
        isUser: false,
        timestamp: new Date(),
        model: availableModels.find(m => m.id === selectedModel)?.display || selectedModel,
        isError: true
      };
      
      setThreads(prev => ({
        ...prev,
        [activeThread.id]: [...(prev[activeThread.id] || []), userMessage, errorResponse]
      }));
    }
  };

  const handleStartThread = (message) => {
    setActiveThread(message);
    // Initialize thread if it doesn't exist
    if (!threads[message.id]) {
      setThreads(prev => ({
        ...prev,
        [message.id]: []
      }));
    }
  };

  const handleCloseThread = () => {
    setActiveThread(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleThreadKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendThreadMessage();
    }
  };

  const addContextFile = () => {
    const fileName = prompt('Enter file name:');
    if (fileName) {
      setContextFiles(prev => [...prev, { id: Date.now(), name: fileName }]);
    }
  };

  const removeContextFile = (id) => {
    setContextFiles(prev => prev.filter(file => file.id !== id));
  };

  // Chat management functions
  const handleNewChat = async () => {
    try {
      // Create a new conversation via API
      const result = await chatService.createConversation(
        "New Conversation",
        selectedProvider || "OpenAI",
        availableModels.find(m => m.id === selectedModel)?.model || "gpt-4o-mini"
      );
      
      if (result.success) {
        const newConversation = chatService.transformConversation(result.data);
        setConversations(prev => [newConversation, ...prev]);
        setCurrentConversationId(newConversation.id);
        setConversationName(newConversation.name);
      }
    } catch (error) {
      console.error('Error creating new conversation:', error);
      // Fall back to local state if API fails
      setCurrentConversationId(null);
      setConversationName("New Conversation");
    }
    
    setMessages([]);
    setThreads({});
    setActiveThread(null);
    setContextFiles([]);
    setReplyingTo(null);
    setShowHistory(false);
  };

  const handleEditName = () => {
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    setIsEditingName(false);
    if (currentConversationId) {
      setConversations(prev => prev.map(conv => 
        conv.id === currentConversationId 
          ? { ...conv, name: conversationName }
          : conv
      ));
    }
  };

  const handleLoadConversation = async (conversation) => {
    setCurrentConversationId(conversation.id);
    setConversationName(conversation.name);
    setActiveThread(null);
    setShowHistory(false);
    setSelectedConversations(new Set());
    setIsMultiSelectMode(false);
    
    // Load messages for this conversation
    try {
      const result = await chatService.getMessages(conversation.id);
      if (result.success) {
        // Transform messages and organize into main messages and threads
        const allMessages = result.data.results || [];
        const mainMessages = [];
        const threadsByParent = {};
        
        allMessages.forEach(apiMessage => {
          const transformedMessage = chatService.transformMessage(apiMessage);
          
          if (apiMessage.parent_message_id) {
            // This is a thread message
            if (!threadsByParent[apiMessage.parent_message_id]) {
              threadsByParent[apiMessage.parent_message_id] = [];
            }
            threadsByParent[apiMessage.parent_message_id].push(transformedMessage);
          } else {
            // This is a main conversation message
            // Add thread messages if they exist
            if (apiMessage.thread_messages && apiMessage.thread_messages.length > 0) {
              threadsByParent[apiMessage.id] = apiMessage.thread_messages.map(threadMsg => 
                chatService.transformMessage(threadMsg)
              );
            }
            mainMessages.push(transformedMessage);
          }
        });
        
        setMessages(mainMessages);
        setThreads(threadsByParent);
      } else {
        console.error('Failed to load messages:', result.error);
        // Set empty arrays if loading fails
        setMessages([]);
        setThreads({});
      }
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      setMessages([]);
      setThreads({});
    }
  };

  const toggleConversationSelection = (conversationId) => {
    const newSelected = new Set(selectedConversations);
    if (newSelected.has(conversationId)) {
      newSelected.delete(conversationId);
    } else {
      newSelected.add(conversationId);
    }
    setSelectedConversations(newSelected);
  };

  const handleDeleteSelected = () => {
    if (selectedConversations.size === 0) return;
    
    setConversations(prev => prev.filter(conv => !selectedConversations.has(conv.id)));
    setSelectedConversations(new Set());
    setIsMultiSelectMode(false);
    
    // If current conversation was deleted, start new chat
    if (selectedConversations.has(currentConversationId)) {
      handleNewChat();
    }
  };

  const filteredConversations = conversations.filter(conv =>
    conv.name.toLowerCase().includes(historySearch.toLowerCase()) ||
    conv.preview.toLowerCase().includes(historySearch.toLowerCase())
  );

  const formatDate = (date) => {
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  // Model selector component
  const ModelSelector = () => (
    <div className="relative" style={{ position: 'relative' }}>
      <button
        onClick={() => setShowModelDropdown(!showModelDropdown)}
        disabled={modelsLoading || modelsError}
        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
          padding: '0.25rem 0.5rem',
          fontSize: '0.75rem',
          color: selectedModel ? '#000000' : '#9ca3af',
          transition: 'color 0.2s',
          border: 'none',
          background: 'transparent',
          cursor: (modelsLoading || modelsError) ? 'not-allowed' : 'pointer',
          opacity: (modelsLoading || modelsError) ? '0.5' : '1',
          fontWeight: selectedModel ? '500' : '400'
        }}
      >
                 <span>
           {modelsLoading ? 'Loading...' : 
            modelsError ? 'Error' : 
            (availableModels.find(m => m.id === selectedModel)?.display || 'Select Model')}
         </span>
        <ChevronDown className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} />
      </button>
      
             {showModelDropdown && !modelsLoading && !modelsError && (
         <div 
           className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-64 model-dropdown"
           style={{
             position: 'absolute',
             top: '100%',
             right: '0',
             marginTop: '0.25rem',
             backgroundColor: '#ffffff',
             border: '1px solid #d1d5db',
             borderRadius: '0.5rem',
             boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
             zIndex: '10',
             minWidth: '16rem',
             maxHeight: '240px',
             overflowY: 'auto',
             scrollBehavior: 'smooth'
           }}
         >
           {availableModels.map(model => (
             <button
               key={model.id}
               onClick={() => {
                 setSelectedModel(model.id);
                 setSelectedProvider(model.provider);
                 setShowModelDropdown(false);
               }}
               className={`w-full text-left px-3 py-3 text-xs hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${selectedModel === model.id ? 'bg-blue-50 text-blue-700' : ''}`}
               style={{
                 width: '100%',
                 textAlign: 'left',
                 padding: '0.75rem',
                 fontSize: '0.75rem',
                 backgroundColor: selectedModel === model.id ? '#eff6ff' : 'transparent',
                 color: selectedModel === model.id ? '#1d4ed8' : 'inherit',
                 border: 'none',
                 cursor: 'pointer'
               }}
             >
               <div style={{ fontWeight: selectedModel === model.id ? '500' : '400', marginBottom: '0.25rem' }}>
                 {model.display}
               </div>
               <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.125rem' }}>
                 {model.name}
               </div>
               <div style={{ fontSize: '0.6rem', color: '#059669', fontWeight: '500' }}>
                 {model.pricing}
               </div>
             </button>
           ))}
         </div>
       )}
      
             {showModelDropdown && modelsError && (
         <div 
           className="absolute top-full right-0 mt-1 bg-white border border-red-300 rounded-lg shadow-lg z-10 min-w-64 p-3"
           style={{
             position: 'absolute',
             top: '100%',
             right: '0',
             marginTop: '0.25rem',
             backgroundColor: '#ffffff',
             border: '1px solid #fca5a5',
             borderRadius: '0.5rem',
             boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
             zIndex: '10',
             minWidth: '16rem',
             padding: '0.75rem'
           }}
         >
          <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>
            {modelsError}
          </div>
        </div>
      )}
    </div>
  );

  // Message action buttons component
  const MessageActions = ({ message }) => (
    <div 
      className="flex items-center gap-2 mt-2"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginTop: '0.5rem'
      }}
    >
      <button
        onClick={() => handleStartThread(message)}
        className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded transition-all duration-200 hover:scale-105 active:scale-95"
        style={{
          padding: '0.375rem',
          color: '#9ca3af',
          borderRadius: '0.25rem',
          transition: 'all 0.2s',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          transform: 'scale(1)'
        }}
        title="Start thread"
        onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
      >
        <Reply className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
      </button>
      <button
        onClick={() => copyToClipboard(message.text)}
        className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded transition-all duration-200 hover:scale-105 active:scale-95"
        style={{
          padding: '0.375rem',
          color: '#9ca3af',
          borderRadius: '0.25rem',
          transition: 'all 0.2s',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          transform: 'scale(1)'
        }}
        title="Copy message"
        onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
      >
        <Copy className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
      </button>
      {!message.isUser && (
        <button
          onClick={() => setShowModelInfo(showModelInfo === message.id ? null : message.id)}
          className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            padding: '0.375rem',
            color: showModelInfo === message.id ? '#2563eb' : '#9ca3af',
            borderRadius: '0.25rem',
            transition: 'all 0.2s',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            transform: 'scale(1)'
          }}
          title="View model info"
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          <Eye className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
        </button>
      )}
    </div>
  );

  // Thread summary component
  const ThreadSummary = ({ message }) => {
    const threadMessages = threads[message.id] || [];
    const threadCount = threadMessages.length;
    
    if (threadCount === 0) return null;
    
    return (
      <div 
        className={`mt-3 inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-full text-sm cursor-pointer hover:bg-blue-100 transition-all duration-300 ${message.animate ? 'animate-bounce' : ''}`}
        style={{
          marginTop: '0.75rem',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#eff6ff',
          color: '#1d4ed8',
          borderRadius: '9999px',
          fontSize: '0.875rem',
          cursor: 'pointer',
          transition: 'all 0.3s',
          transform: message.animate ? 'scale(1.1)' : 'scale(1)'
        }}
        onClick={() => handleStartThread(message)}
        title="View thread replies"
      >
        <MessageSquare className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
        <span>{threadCount} {threadCount === 1 ? 'reply' : 'replies'}</span>
      </div>
    );
  };

  const isEmpty = messages.length === 0;
  const hasStartedConversation = messages.length > 0;

  return (
    <div 
      className="flex flex-col bg-white text-black font-mono" 
      style={{ 
        height: 'calc(100vh - 40px)',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: 'monospace',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <style>{`
        /* Mobile hamburger menu button */
        .mobile-hamburger-btn {
          display: none;
          align-items: center;
          justify-content: center;
          width: 3rem;
          height: 3rem;
          background: transparent;
          border: none;
          color: #374151;
          cursor: pointer;
          border-radius: 0.5rem;
          transition: all 0.2s;
          margin-right: 1rem;
        }

        .mobile-hamburger-btn:hover {
          background: #f3f4f6;
          color: #111827;
        }

        /* Mobile header */
        .mobile-header {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 3.5rem;
          background: white;
          border-bottom: 1px solid #e5e7eb;
          z-index: 1100;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .mobile-header-content {
          display: flex;
          align-items: center;
          padding: 0 1rem;
          height: 100%;
        }

        .mobile-header-content h1 {
          font-size: 1.25rem;
          margin: 0;
          font-weight: 700;
          color: #111827;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
        }

        .mobile-header-content .header-title-icon {
          width: 1.5rem;
          height: 1.5rem;
        }

        /* Mobile navigation modal */
        .mobile-nav-modal {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100vw;
          max-width: 100vw;
          background: white;
          border-radius: 1rem 1rem 0 0;
          box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
          max-height: 80vh;
          overflow-y: auto;
          z-index: 10000;
          transform: translateY(100%);
          transition: transform 0.3s ease-out;
          padding-top: 0;
          box-sizing: border-box;
        }

        .mobile-nav-modal.show {
          transform: translateY(0);
        }

        .mobile-nav-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 1rem 1rem 1rem;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 1rem;
        }

        .mobile-nav-header h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
        }

        .mobile-nav-close {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2rem;
          height: 2rem;
          background: transparent;
          border: none;
          color: #6b7280;
          cursor: pointer;
          border-radius: 0.375rem;
          transition: all 0.2s;
        }

        .mobile-nav-close:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .mobile-nav-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          padding: 0 1rem;
        }

        .mobile-nav-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: transparent;
          border: none;
          cursor: pointer;
          border-radius: 0.5rem;
          text-decoration: none;
          color: #374151;
          transition: all 0.2s;
          font-size: 1rem;
          font-weight: 500;
        }

        .mobile-nav-item:hover {
          background: #f9fafb;
          color: #111827;
        }

        .mobile-nav-icon {
          width: 1.5rem;
          height: 1.5rem;
          object-fit: contain;
        }

        .mobile-nav-title {
          font-weight: 500;
        }

        /* Dropdown backdrop */
        .dropdown-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9999;
          opacity: 0;
          transition: opacity 0.3s ease-out;
        }

        .dropdown-backdrop.show {
          opacity: 1;
        }

        /* Modal gripper for pull-to-dismiss */
        .modal-gripper {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 1rem 0 0.75rem 0;
          position: relative;
          cursor: grab;
          background: white;
          border-radius: 1rem 1rem 0 0;
          min-height: 3rem;
        }

        .modal-gripper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: transparent;
          z-index: 1;
        }

        .gripper-line {
          width: 3rem;
          height: 0.375rem;
          background: #9ca3af;
          border-radius: 0.1875rem;
          transition: background-color 0.2s;
          z-index: 2;
          position: relative;
        }

        .modal-gripper:hover .gripper-line {
          background: #6b7280;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }
        
        .animate-bounce {
          animation: bounce 1s infinite;
        }
        
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
        
        /* Custom scrollbar styling for model dropdown */
        .model-dropdown {
          position: relative;
        }
        
        .model-dropdown::-webkit-scrollbar {
          width: 6px;
        }
        
        .model-dropdown::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        
        .model-dropdown::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        
        .model-dropdown::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        
        /* Fade effect at bottom to indicate more content */
        .model-dropdown::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 20px;
          background: linear-gradient(transparent, rgba(255, 255, 255, 0.8));
          pointer-events: none;
          border-radius: 0 0 0.5rem 0.5rem;
        }
        
        @media (max-width: 768px) {
          /* Show mobile header and hamburger menu */
          .mobile-header {
            display: block;
          }

          .mobile-hamburger-btn {
            display: flex !important;
            width: 3rem;
            height: 3rem;
            margin-right: 0.75rem;
            flex-shrink: 0;
          }

          .mobile-hamburger-btn svg {
            width: 1.5rem;
            height: 1.5rem;
          }

          /* Adjust page layout for mobile header */
          .flex.flex-col.bg-white.text-black.font-mono {
            padding-top: 3.5rem;
          }

          .thread-mobile {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            z-index: 50 !important;
            width: 100% !important;
          }
          
          .mobile-touch {
            min-height: 48px;
            min-width: 48px;
          }
          
          .mobile-text {
            font-size: 16px !important;
          }
        }
      `}</style>
      
      {/* Mobile Header */}
      <div className="mobile-header" style={{ border: '3px solid green' }}>
        <div className="mobile-header-content">
          {/* Debug indicator */}
          <div style={{ 
            background: 'orange', 
            color: 'black', 
            padding: '5px', 
            fontSize: '10px',
            marginRight: '10px'
          }}>
            MOBILE HEADER
          </div>
          {/* Mobile hamburger menu */}
          <button
            className="mobile-hamburger-btn"
            onClick={() => setShowMobileNav(true)}
            aria-label="Open navigation menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h1>
            <img src="/images/chat.png" alt="Chat" className="header-title-icon" />
            Chat
          </h1>
        </div>
      </div>

      {/* Header */}
        <div 
          className="flex justify-between items-center p-4 border-b border-gray-200"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem',
            borderBottom: '1px solid #e5e7eb'
          }}
        >
        {/* Left side - conversation name (only show if conversation started) */}
        <div className="flex items-center gap-3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {hasStartedConversation && (
            <>
              {/* Editable conversation name */}
              {isEditingName ? (
                <input
                  type="text"
                  value={conversationName}
                  onChange={(e) => setConversationName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') {
                      setIsEditingName(false);
                      setConversationName(currentConversationId ? 
                        conversations.find(c => c.id === currentConversationId)?.name || "New Conversation" 
                        : "New Conversation"
                      );
                    }
                  }}
                  className="text-lg font-medium bg-transparent border-b border-gray-300 focus:border-black outline-none"
                  style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: '500',
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid #d1d5db',
                    outline: 'none',
                    minWidth: '200px'
                  }}
                  autoFocus
                />
              ) : (
                <h1 className="text-lg font-medium" style={{ fontSize: '1.125rem', fontWeight: '500' }}>
                  {conversationName}
                </h1>
              )}
              
              <button
                onClick={handleEditName}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                style={{
                  padding: '0.25rem',
                  borderRadius: '0.375rem',
                  transition: 'background-color 0.2s',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer'
                }}
                title="Edit conversation name"
              >
                <Edit2 className="w-4 h-4 text-gray-400" style={{ width: '1rem', height: '1rem', color: '#9ca3af' }} />
              </button>
            </>
          )}
          
          {/* If no conversation started, show "Chat" title */}
          {!hasStartedConversation && (
            <h1 className="text-lg font-medium" style={{ fontSize: '1.125rem', fontWeight: '500' }}>
              Chat
            </h1>
          )}
        </div>
        
        {/* Right side - action buttons */}
        <div 
          className="flex items-center gap-2" 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem'
          }}
        >
          {/* New Chat Button */}
          <button 
            onClick={handleNewChat}
            className="p-2 hover:bg-gray-100 hover:shadow-sm rounded-md transition-all duration-200 mobile-touch"
            style={{
              padding: '0.5rem',
              borderRadius: '0.375rem',
              transition: 'all 0.2s',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#000000',
              outline: 'none'
            }}
            title="New chat"
            onMouseEnter={(e) => e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)'}
            onMouseLeave={(e) => e.target.style.boxShadow = 'none'}
          >
            <Plus className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem', color: '#000000' }} />
          </button>
          
          {/* History Button */}
          <button 
            onClick={() => setShowHistory(true)}
            className="p-2 hover:bg-gray-100 hover:shadow-sm rounded-md transition-all duration-200 mobile-touch"
            style={{
              padding: '0.5rem',
              borderRadius: '0.375rem',
              transition: 'all 0.2s',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#000000',
              outline: 'none'
            }}
            title="Chat history"
            onMouseEnter={(e) => e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)'}
            onMouseLeave={(e) => e.target.style.boxShadow = 'none'}
          >
            <History className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem', color: '#000000' }} />
        </button>
        </div>
      </div>

      {/* History Sidebar */}
      {showHistory && (
        <div 
          className="fixed inset-0 z-50"
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            zIndex: '50'
          }}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              right: '0',
              bottom: '0',
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            onClick={() => setShowHistory(false)}
          />
          
          {/* Sidebar */}
          <div 
            className="absolute right-0 top-0 h-full w-96 bg-white shadow-lg transform transition-transform duration-300 ease-out"
            style={{
              position: 'absolute',
              right: '0',
              top: '0',
              height: '100%',
              width: '24rem',
              backgroundColor: '#ffffff',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              transform: 'translateX(0)',
              transition: 'transform 300ms ease-out'
            }}
          >
            {/* Sidebar Header */}
            <div 
              className="flex justify-between items-center p-4 border-b border-gray-200"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                borderBottom: '1px solid #e5e7eb'
              }}
            >
              <h2 className="text-lg font-medium" style={{ fontSize: '1.125rem', fontWeight: '500' }}>Chat History</h2>
              
              <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {isMultiSelectMode && (
                  <button
                    onClick={handleDeleteSelected}
                    disabled={selectedConversations.size === 0}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      padding: '0.5rem',
                      color: '#dc2626',
                      borderRadius: '0.375rem',
                      transition: 'background-color 0.2s',
                      border: 'none',
                      background: 'transparent',
                      cursor: selectedConversations.size === 0 ? 'not-allowed' : 'pointer',
                      opacity: selectedConversations.size === 0 ? '0.5' : '1'
                    }}
                    title="Delete selected conversations"
                  >
                    <Trash2 className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                  </button>
                )}
                
                <button
                  onClick={() => {
                    setIsMultiSelectMode(!isMultiSelectMode);
                    setSelectedConversations(new Set());
                  }}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    transition: 'background-color 0.2s',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                  title={isMultiSelectMode ? "Cancel selection" : "Select conversations"}
                >
                  {isMultiSelectMode ? (
                    <X className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                  ) : (
                    <MoreVertical className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                  )}
                </button>
                
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    transition: 'background-color 0.2s',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                >
                  <X className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                </button>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="p-4" style={{ padding: '1rem' }}>
              <div 
                className="relative"
                style={{ position: 'relative' }}
              >
                <Search 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '1rem',
                    height: '1rem',
                    color: '#9ca3af'
                  }}
                />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:border-black focus:outline-none"
                  style={{
                    width: '100%',
                    paddingLeft: '2.5rem',
                    paddingRight: '1rem',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.5rem',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
            
            {/* Conversation List */}
            <div 
              className="flex-1 overflow-y-auto"
              style={{
                flex: '1',
                overflowY: 'auto'
              }}
            >
              {conversationsLoading ? (
                <div 
                  className="text-center text-gray-500 mt-8"
                  style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    marginTop: '2rem'
                  }}
                >
                  Loading conversations...
                </div>
              ) : conversationsError ? (
                <div 
                  className="text-center text-red-500 mt-8"
                  style={{
                    textAlign: 'center',
                    color: '#dc2626',
                    marginTop: '2rem'
                  }}
                >
                  Error: {conversationsError}
                </div>
              ) : filteredConversations.length === 0 ? (
                <div 
                  className="text-center text-gray-500 mt-8"
                  style={{
                    textAlign: 'center',
                    color: '#6b7280',
                    marginTop: '2rem'
                  }}
                >
                  {conversations.length === 0 ? 'No conversation history' : 'No conversations found'}
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`flex items-start gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                      currentConversationId === conversation.id ? 'bg-blue-50' : ''
                    }`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      padding: '1rem',
                      borderBottom: '1px solid #f3f4f6',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      backgroundColor: currentConversationId === conversation.id ? '#eff6ff' : (isMultiSelectMode && selectedConversations.has(conversation.id) ? '#f3f4f6' : 'transparent')
                    }}
                    onClick={() => {
                      if (isMultiSelectMode) {
                        toggleConversationSelection(conversation.id);
                      } else {
                        handleLoadConversation(conversation);
                      }
                    }}
                  >
                    {/* Selection checkbox for multi-select mode */}
                    {isMultiSelectMode && (
                      <div 
                        className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                          selectedConversations.has(conversation.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                        }`}
                        style={{
                          width: '1.25rem',
                          height: '1.25rem',
                          border: selectedConversations.has(conversation.id) ? '2px solid #2563eb' : '2px solid #d1d5db',
                          borderRadius: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: selectedConversations.has(conversation.id) ? '#2563eb' : 'transparent'
                        }}
                      >
                        {selectedConversations.has(conversation.id) && (
                          <Check className="w-3 h-3 text-white" style={{ width: '0.75rem', height: '0.75rem', color: '#ffffff' }} />
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0" style={{ flex: '1', minWidth: '0' }}>
                      <h3 
                        className="text-sm font-medium text-black truncate"
                        style={{
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#000000',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {conversation.name}
                      </h3>
                      <p 
                        className="text-xs text-gray-600 truncate mt-1"
                        style={{
                          fontSize: '0.75rem',
                          color: '#4b5563',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          marginTop: '0.25rem'
                        }}
                      >
                        {conversation.preview}
                      </p>
                      <div 
                        className="flex justify-between text-xs text-gray-400 mt-2"
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontSize: '0.75rem',
                          color: '#9ca3af',
                          marginTop: '0.5rem'
                        }}
                      >
                        <span>Created: {formatDate(conversation.createdAt)}</span>
                        <span>Last: {formatDate(conversation.lastMessage)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div 
        className="flex-1 flex min-h-0"
        style={{
          flex: '1',
          display: 'flex',
          minHeight: '0'
        }}
      >
        {/* Main Chat Area */}
        <div 
          className={`flex-1 flex flex-col min-h-0 ${activeThread ? 'w-1/2 lg:w-1/2 md:w-full' : 'w-full'}`}
          style={{
            flex: activeThread ? '0 0 50%' : '1',
            display: 'flex',
            flexDirection: 'column',
            minHeight: '0',
            transition: 'all 0.3s ease'
          }}
        >
        {isEmpty ? (
          /* Centered Initial State */
            <div 
              className="flex-1 flex items-center justify-center"
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div 
                className="w-full max-w-2xl px-6"
                style={{
                  width: '100%',
                  maxWidth: '42rem',
                  paddingLeft: '1.5rem',
                  paddingRight: '1.5rem'
                }}
              >
                                  <div 
                    className="text-center mb-12 relative"
                    style={{
                      textAlign: 'center',
                      marginBottom: '3rem',
                      position: 'relative'
                    }}
                  >
                    {/* Animated background element */}
                    <div 
                      className="absolute inset-0 bg-gradient-to-br from-gray-50 to-transparent opacity-30 rounded-3xl animate-pulse"
                      style={{
                        position: 'absolute',
                        top: '-2rem',
                        left: '-2rem',
                        right: '-2rem',
                        bottom: '-2rem',
                        background: 'linear-gradient(135deg, #f9fafb 0%, transparent 100%)',
                        opacity: '0.3',
                        borderRadius: '1.5rem',
                        animation: 'pulse 4s ease-in-out infinite',
                        zIndex: '-1'
                      }}
                    />
                    <h2 
                      className="text-2xl font-light mb-2 relative z-10"
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: '300',
                        marginBottom: '0.5rem',
                        position: 'relative',
                        zIndex: '10'
                      }}
                    >
                      How can I help?
                    </h2>
                    <p 
                      className="text-gray-500 text-sm relative z-10"
                      style={{
                        color: '#6b7280',
                        fontSize: '0.875rem',
                        position: 'relative',
                        zIndex: '10'
                      }}
                    >
                      Ask me anything
                    </p>
              </div>
              
              {/* Initial Input */}
                <div className="relative" style={{ position: 'relative' }}>
                {/* Context Panel */}
                {showContextPanel && (
                    <div 
                      className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg p-4 shadow-lg z-10 transform transition-all duration-200 ease-out"
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '0',
                        right: '0',
                        marginBottom: '0.5rem',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        zIndex: '10'
                      }}
                    >
                      <div 
                        className="flex items-center justify-between mb-3"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '0.75rem'
                        }}
                      >
                        <h3 className="text-sm font-medium" style={{ fontSize: '0.875rem', fontWeight: '500' }}>Context Files</h3>
                      <button
                        onClick={addContextFile}
                        className="text-gray-500 hover:text-black text-xs flex items-center gap-1 px-2 py-1 rounded-md transition-colors"
                          style={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.375rem',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer'
                          }}
                        >
                          <Plus className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} />
                        Add
                      </button>
                    </div>
                      <div 
                        className="flex flex-wrap gap-2"
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '0.5rem'
                        }}
                      >
                      {contextFiles.map(file => (
                          <div 
                            key={file.id} 
                            className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1 rounded-full text-xs"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              backgroundColor: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '9999px',
                              fontSize: '0.75rem'
                            }}
                          >
                            <File className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} />
                          <span>{file.name}</span>
                          <button
                            onClick={() => removeContextFile(file.id)}
                            className="text-gray-500 hover:text-black ml-1"
                              style={{
                                color: '#6b7280',
                                marginLeft: '0.25rem',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer'
                              }}
                            >
                              <X className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                  <div 
                    className="border border-gray-300 rounded-lg focus-within:border-black transition-colors"
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      transition: 'border-color 0.2s'
                    }}
                  >
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type a message..."
                    className="w-full bg-transparent px-4 py-4 pr-32 resize-none focus:outline-none min-h-[60px] max-h-32"
                      style={{
                        width: '100%',
                        backgroundColor: 'transparent',
                        padding: '1rem',
                        paddingRight: '8rem',
                        resize: 'none',
                        outline: 'none',
                        minHeight: '60px',
                        maxHeight: '8rem',
                        border: 'none',
                        fontFamily: 'inherit'
                      }}
                    rows={1}
                  />
                  
                    <div 
                      className="absolute right-3 top-3 flex items-center gap-2"
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                    <button
                      onClick={() => setShowContextPanel(!showContextPanel)}
                        className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-black"
                        style={{
                          padding: '0.375rem',
                          borderRadius: '0.375rem',
                          transition: 'color 0.2s',
                          color: '#9ca3af',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer'
                        }}
                    >
                      @
                    </button>
                    
                    {/* Model Selector */}
                    <ModelSelector />
                    
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || !selectedModel || isSending}
                      className="p-1.5 bg-black hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md transition-colors"
                        style={{
                          padding: '0.375rem',
                          backgroundColor: (inputValue.trim() && selectedModel && !isSending) ? '#000000' : '#d1d5db',
                          color: '#ffffff',
                          borderRadius: '0.375rem',
                          transition: 'background-color 0.2s',
                          border: 'none',
                          cursor: (inputValue.trim() && selectedModel && !isSending) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <Send className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Chat Messages */
            <div 
              className="flex-1 flex flex-col min-h-0"
              style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '0'
              }}
            >
              <div 
                ref={chatContainerRef} 
                className="flex-1 overflow-y-auto p-4 space-y-6"
                style={{
                  flex: '1',
                  overflowY: 'auto',
                  padding: '1rem'
                }}
              >
              {messages.map((message) => (
                  <div key={message.id} className="space-y-4" style={{ marginBottom: '2rem' }}>
                    <div className={`${message.isUser ? 'flex justify-end' : ''}`} style={message.isUser ? { display: 'flex', justifyContent: 'flex-end' } : {}}>
                    {message.isUser ? (
                        <div 
                          className="max-w-2xl bg-black text-white rounded-lg px-4 py-3 shadow-lg"
                          style={{
                            maxWidth: '42rem',
                            backgroundColor: '#000000',
                            color: '#ffffff',
                            borderRadius: '0.5rem',
                            padding: '0.75rem 1rem',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                          }}
                        >
                          <div className="text-sm leading-relaxed" style={{ fontSize: '0.875rem', lineHeight: '1.625' }}>
                            {renderMarkdown(message.text)}
                        </div>
                            </div>
                          ) : (
                        <div className={`max-w-none rounded-lg p-4 ${message.isError ? 'bg-red-50 bg-opacity-50' : 'bg-gray-50 bg-opacity-30'}`} style={{ backgroundColor: message.isError ? 'rgba(254, 242, 242, 0.5)' : 'rgba(249, 250, 251, 0.3)', borderRadius: '0.5rem', padding: '1rem' }}>
                              <div className="prose prose-sm max-w-none">
                            <div 
                              className={`leading-relaxed ${message.isError ? 'text-red-600' : 'text-black'}`}
                              style={{
                                lineHeight: '1.625',
                                color: message.isError ? '#dc2626' : '#000000'
                              }}
                            >
                              {renderMarkdown(message.text)}
                              </div>
                            </div>
                          
                          {/* Model info popup for AI responses */}
                          {!message.isUser && showModelInfo === message.id && (
                            <div style={{ 
                              marginTop: '0.5rem', 
                              marginBottom: '0.5rem',
                              padding: '0.5rem',
                              backgroundColor: '#f9fafb',
                              borderRadius: '0.375rem',
                              border: '1px solid #e5e7eb'
                            }}>
                              <div style={{ fontSize: '0.65rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: '500' }}>
                                Model Used:
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#374151', fontWeight: '500' }}>
                                {message.modelUsed || message.model}
                              </div>
                              {message.modelUsed && (
                                <div style={{ fontSize: '0.6rem', color: '#6b7280', marginTop: '0.125rem' }}>
                                  Provider: {message.modelUsed.split(' ')[0]}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <MessageActions message={message} />
                          <ThreadSummary message={message} />
                    </div>
                  )}
                    </div>
                </div>
              ))}
              
              {/* AI Thinking Indicator */}
              {isAiThinking && (
                <div className="flex items-center gap-3 text-gray-500 animate-pulse" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#6b7280' }}>
                  <div className="flex space-x-1" style={{ display: 'flex', gap: '0.25rem' }}>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#9ca3af', borderRadius: '50%', animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#9ca3af', borderRadius: '50%', animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ width: '0.5rem', height: '0.5rem', backgroundColor: '#9ca3af', borderRadius: '50%', animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm">Thinking...</span>
                </div>
              )}
            </div>
            
            {/* Bottom Input */}
              <div 
                className="border-t border-gray-200 p-4"
                style={{
                  borderTop: '1px solid #e5e7eb',
                  padding: '1rem'
                }}
              >
                <div 
                  className="relative max-w-4xl mx-auto"
                  style={{
                    position: 'relative',
                    maxWidth: '56rem',
                    margin: '0 auto'
                  }}
                >
                  <div 
                    className="border border-gray-300 rounded-lg focus-within:border-black transition-colors"
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '0.5rem',
                      transition: 'border-color 0.2s'
                    }}
                  >
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyPress}
                    placeholder="Type a message..."
                    className="w-full bg-transparent px-4 py-4 pr-32 resize-none focus:outline-none min-h-[60px] max-h-32 mobile-text"
                      style={{
                        width: '100%',
                        backgroundColor: 'transparent',
                        padding: '1rem',
                        paddingRight: '8rem',
                        resize: 'none',
                        outline: 'none',
                        minHeight: '60px',
                        maxHeight: '8rem',
                        border: 'none',
                        fontFamily: 'inherit'
                      }}
                    rows={1}
                  />
                  
                    <div 
                      className="absolute right-3 top-3 flex items-center gap-2"
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                    <button
                      onClick={() => setShowContextPanel(!showContextPanel)}
                        className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-black"
                        style={{
                          padding: '0.375rem',
                          borderRadius: '0.375rem',
                          transition: 'color 0.2s',
                          color: '#9ca3af',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer'
                        }}
                    >
                      @
                    </button>
                    
                    {/* Model Selector */}
                    <ModelSelector />
                    
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || !selectedModel || isSending}
                      className="p-1.5 bg-black hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md transition-colors"
                        style={{
                          padding: '0.375rem',
                          backgroundColor: (inputValue.trim() && selectedModel && !isSending) ? '#000000' : '#d1d5db',
                          color: '#ffffff',
                          borderRadius: '0.375rem',
                          transition: 'background-color 0.2s',
                          border: 'none',
                          cursor: (inputValue.trim() && selectedModel && !isSending) ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <Send className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                    </button>
                  </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Thread Sidebar */}
        {activeThread && (
          <div 
            className={`w-1/2 lg:w-1/2 md:w-full border-l border-gray-200 flex flex-col animate-slide-in ${typeof window !== 'undefined' && window.innerWidth < 768 ? 'thread-mobile' : ''}`}
            style={{
              width: typeof window !== 'undefined' && window.innerWidth < 768 ? '100%' : '50%',
              borderLeft: '1px solid #e5e7eb',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideIn 0.3s ease-out',
              ...(typeof window !== 'undefined' && window.innerWidth < 768 ? {
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                zIndex: '50',
                backgroundColor: '#ffffff'
              } : {})
            }}
          >
            {/* Thread Header */}
            <div 
              className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 shadow-sm"
              style={{
                padding: '1rem',
                borderBottom: '1px solid #e5e7eb',
                background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="flex items-center justify-between" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <h3 className="font-medium text-sm" style={{ fontWeight: '500', fontSize: '0.875rem' }}>Thread</h3>
                  <p className="text-xs text-gray-500 mt-1" style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    {activeThread.text.slice(0, 50)}...
                  </p>
                </div>
                <div className="flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button 
                    onClick={handleCloseThread}
                    className="p-2 text-gray-500 hover:text-black hover:bg-gray-200 rounded-md transition-all duration-200 hover:scale-110"
                    style={{
                      padding: '0.5rem',
                      color: '#6b7280',
                      borderRadius: '0.375rem',
                      transition: 'all 0.2s',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      transform: 'scale(1)'
                    }}
                    title="Back to main chat"
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  >
                    <ChevronLeft className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} />
                  </button>
                </div>
              </div>
            </div>

            {/* Thread Messages */}
            <div 
              className="flex-1 overflow-y-auto p-4"
              style={{
                flex: '1',
                overflowY: 'auto',
                padding: '1rem'
              }}
            >
              {/* Original Message */}
              <div className="mb-4 pb-4 border-b border-gray-100" style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #f3f4f6' }}>
                <div className={`${activeThread.isUser ? 'flex justify-end' : ''}`} style={activeThread.isUser ? { display: 'flex', justifyContent: 'flex-end' } : {}}>
                  {activeThread.isUser ? (
                    <div 
                      className="max-w-lg bg-black text-white rounded-lg px-3 py-2 text-sm"
                      style={{
                        maxWidth: '32rem',
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        borderRadius: '0.5rem',
                        padding: '0.5rem 0.75rem',
                        fontSize: '0.875rem'
                      }}
                    >
                      {renderMarkdown(activeThread.text)}
                    </div>
                  ) : (
                    <div 
                      className="text-sm text-black"
                      style={{
                        fontSize: '0.875rem',
                        color: '#000000',
                        lineHeight: '1.5'
                      }}
                    >
                      {renderMarkdown(activeThread.text)}
                    </div>
                  )}
                </div>
              </div>

              {/* Thread Replies */}
              {(threads[activeThread.id] || []).map((threadMessage) => (
                <div key={threadMessage.id} className="mb-4" style={{ marginBottom: '1rem' }}>
                  <div className={`${threadMessage.isUser ? 'flex justify-end' : ''}`} style={threadMessage.isUser ? { display: 'flex', justifyContent: 'flex-end' } : {}}>
                    {threadMessage.isUser ? (
                      <div 
                        className="max-w-lg bg-black text-white rounded-lg px-3 py-2 text-sm"
                        style={{
                          maxWidth: '32rem',
                          backgroundColor: '#000000',
                          color: '#ffffff',
                          borderRadius: '0.5rem',
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.875rem'
                        }}
                      >
                        {renderMarkdown(threadMessage.text)}
                      </div>
                    ) : (
                      <div>
                        <div 
                          className={`text-sm mb-2 ${threadMessage.isError ? 'text-red-600' : 'text-black'}`}
                          style={{
                            fontSize: '0.875rem',
                            color: threadMessage.isError ? '#dc2626' : '#000000',
                            marginBottom: '0.5rem',
                            lineHeight: '1.5'
                          }}
                        >
                          {renderMarkdown(threadMessage.text)}
                        </div>
                        
                        {/* Model info for thread responses - only show on AI messages */}
                        {!threadMessage.isUser && showModelInfo === `thread-${threadMessage.id}` && (
                          <div style={{ 
                            marginBottom: '0.5rem',
                            padding: '0.375rem',
                            backgroundColor: '#f9fafb',
                            borderRadius: '0.25rem',
                            border: '1px solid #e5e7eb'
                          }}>
                            <div style={{ fontSize: '0.6rem', color: '#6b7280', marginBottom: '0.125rem', fontWeight: '500' }}>
                              Model Used:
                            </div>
                            <div style={{ fontSize: '0.65rem', color: '#374151', fontWeight: '500' }}>
                              {threadMessage.modelUsed || threadMessage.model}
                            </div>
                            {threadMessage.modelUsed && (
                              <div style={{ fontSize: '0.55rem', color: '#6b7280', marginTop: '0.125rem' }}>
                                Provider: {threadMessage.modelUsed.split(' ')[0]}
                              </div>
                            )}
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <button
                            onClick={() => copyToClipboard(threadMessage.text)}
                            className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded transition-all duration-200 hover:scale-105 active:scale-95"
                            style={{
                              padding: '0.375rem',
                              color: '#9ca3af',
                              borderRadius: '0.25rem',
                              transition: 'all 0.2s',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              transform: 'scale(1)'
                            }}
                            title="Copy message"
                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                          >
                            <Copy className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                          </button>
                          {!threadMessage.isUser && (
                            <button
                              onClick={() => setShowModelInfo(showModelInfo === `thread-${threadMessage.id}` ? null : `thread-${threadMessage.id}`)}
                              className="p-1.5 text-gray-400 hover:text-black hover:bg-gray-100 rounded transition-all duration-200 hover:scale-105 active:scale-95"
                              style={{
                                padding: '0.375rem',
                                color: showModelInfo === `thread-${threadMessage.id}` ? '#2563eb' : '#9ca3af',
                                borderRadius: '0.25rem',
                                transition: 'all 0.2s',
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                transform: 'scale(1)'
                              }}
                              title="View model info"
                              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                            >
                              <Eye className="w-4 h-4" style={{ width: '1rem', height: '1rem' }} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Thread Input */}
            <div 
              className="p-4 border-t border-gray-200"
              style={{
                padding: '1rem',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              <div 
                className="relative border border-gray-300 rounded-lg focus-within:border-black transition-colors"
                style={{
                  position: 'relative',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.5rem',
                  transition: 'border-color 0.2s'
                }}
              >
                <textarea
                  ref={threadInputRef}
                  value={threadInputValue}
                  onChange={(e) => setThreadInputValue(e.target.value)}
                  onKeyDown={handleThreadKeyPress}
                  placeholder="Reply to thread..."
                  className="w-full bg-transparent px-4 py-4 pr-16 resize-none focus:outline-none min-h-[60px] max-h-32"
                  style={{
                    width: '100%',
                    backgroundColor: 'transparent',
                    padding: '1rem',
                    paddingRight: '4rem',
                    resize: 'none',
                    outline: 'none',
                    minHeight: '60px',
                    maxHeight: '8rem',
                    border: 'none',
                    fontFamily: 'inherit',
                    fontSize: '0.875rem'
                  }}
                  rows={1}
                />
                
                <div 
                  className="absolute right-3 top-3"
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    top: '0.75rem'
                  }}
                >
                  <button
                    onClick={handleSendThreadMessage}
                    disabled={!threadInputValue.trim() || !selectedModel}
                    className="p-1.5 bg-black hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md transition-colors"
                    style={{
                      padding: '0.375rem',
                      backgroundColor: (threadInputValue.trim() && selectedModel) ? '#000000' : '#d1d5db',
                      color: '#ffffff',
                      borderRadius: '0.375rem',
                      transition: 'background-color 0.2s',
                      border: 'none',
                      cursor: (threadInputValue.trim() && selectedModel) ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <Send className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>



      {/* Mobile Navigation Bottom Sheet */}
      {showMobileNav && (
        <>
          <div 
            className="dropdown-backdrop show" 
            onClick={() => setShowMobileNav(false)}
          />
          <div 
            className="mobile-nav-modal show"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="modal-gripper">
              <div className="gripper-line"></div>
            </div>
            <div className="mobile-nav-header">
              <h3>Navigation</h3>
              <button 
                onClick={() => setShowMobileNav(false)}
                className="mobile-nav-close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="mobile-nav-list">
              {navigationItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className="mobile-nav-item"
                  onClick={() => setShowMobileNav(false)}
                >
                  <img 
                    src={item.icon} 
                    alt={`${item.title} Icon`}
                    className="mobile-nav-icon"
                  />
                  <span className="mobile-nav-title">{item.title}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatDemo;